import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import Loader from './Loader';

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSort, faSortUp, faSortDown } from "@fortawesome/free-solid-svg-icons";

// Clean date format check - checks standard formats (YYYY-MM-DD, MM/DD/YYYY, Month Day, Year, etc.)
const isDateString = (str) => {
  if (typeof str !== 'string') return false;
  const s = str.trim();
  if (!s) return false;
  if (/^\d{4}[-/]\d{1,2}[-/]\d{1,2}/.test(s)) return !isNaN(Date.parse(s));
  if (/^\d{1,2}[-/]\d{1,2}[-/]\d{2,4}/.test(s)) return !isNaN(Date.parse(s));
  if (/^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]* \d{1,2},? \d{4}/i.test(s)) return !isNaN(Date.parse(s));
  return false;
};

// Numeric/currency parser - strips currency symbols, percent, commas
const parseNumericValue = (val) => {
  if (typeof val === 'number') return val;
  if (typeof val !== 'string') return NaN;
  const cleaned = val.replace(/[$,%]/g, '').trim();
  if (cleaned === '') return NaN;
  const num = Number(cleaned);
  return isNaN(num) ? NaN : num;
};

// Extract comparable string/primitive from potentially nested or element value
const extractComparableValue = (val) => {
  if (val === null || val === undefined) return '';
  if (typeof val === 'number' || typeof val === 'boolean' || val instanceof Date) return val;
  if (typeof val === 'string') return val;
  if (React.isValidElement(val)) {
    if (typeof val.props?.children === 'string' || typeof val.props?.children === 'number') {
      return String(val.props.children);
    }
  }
  return String(val);
};

// Smart comparator for numbers, currencies, dates, and natural string sorting
const compareSmartValues = (rawA, rawB) => {
  const a = extractComparableValue(rawA);
  const b = extractComparableValue(rawB);

  if (a === b) return 0;
  if (a === null || a === undefined || a === '') return 1;
  if (b === null || b === undefined || b === '') return -1;

  // Numeric / currency comparison
  const numA = parseNumericValue(a);
  const numB = parseNumericValue(b);
  if (!isNaN(numA) && !isNaN(numB)) {
    return numA - numB;
  }

  // Date comparison
  const isDateA = a instanceof Date || isDateString(a);
  const isDateB = b instanceof Date || isDateString(b);
  if (isDateA && isDateB) {
    const timeA = a instanceof Date ? a.getTime() : Date.parse(a);
    const timeB = b instanceof Date ? b.getTime() : Date.parse(b);
    if (!isNaN(timeA) && !isNaN(timeB)) {
      return timeA - timeB;
    }
  }

  // Natural string locale comparison
  return String(a).trim().localeCompare(String(b).trim(), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
};

// Helper to extract a column's raw value from a row
const getColumnRawValue = (row, column) => {
  if (!row) return undefined;
  if (typeof column.accessorFn === 'function') {
    return column.accessorFn(row);
  }
  if (typeof column.columnDef?.accessorFn === 'function') {
    return column.columnDef.accessorFn(row);
  }
  const key = column.columnDef?.accessorKey || column.id;
  if (!key) return undefined;
  if (typeof key === 'string' && key.includes('.')) {
    return key.split('.').reduce((obj, part) => (obj ? obj[part] : undefined), row);
  }
  return row[key];
};

// Check if the current data is already in ascending order for this column
const checkIfAlreadyAscending = (data, column) => {
  if (!data || data.length < 2) return false;
  let hasDiff = false;
  let prevVal = undefined;

  for (let i = 0; i < data.length; i++) {
    const rawVal = getColumnRawValue(data[i], column);
    if (rawVal === null || rawVal === undefined || rawVal === '') continue;

    if (prevVal !== undefined) {
      const cmp = compareSmartValues(prevVal, rawVal);
      if (cmp > 0) return false;
      if (cmp < 0) hasDiff = true;
    }
    prevVal = rawVal;
  }
  return hasDiff;
};

// Smart comparator for TanStack Table
const smartSortingFn = (rowA, rowB, columnId) => {
  const a = rowA.getValue(columnId);
  const b = rowB.getValue(columnId);
  return compareSmartValues(a, b);
};

// Reusable TanStack Table Component
// Props:
// - data: Array of data to display
// - columns: Column definitions for the table
// - loading: Show loader when true
// - noDataMessage: Custom message when no data (default: "No Data Found")
// - enableSorting: Enable sorting globally (default: true)
// - tableClassName: Additional CSS classes for table
// - initialSorting: Initial sorting state (default: [])
const TanStackTable = ({
  data = [],
  columns = [],
  loading = false,
  noDataMessage = "No Data Found",
  enableSorting = true,
  tableClassName = "table table-bordered table-hover",
  initialSorting = [],
  rowStyle = null
}) => {
  const [sorting, setSorting] = useState(initialSorting);
  const [showNoData, setShowNoData] = useState(false);

  // Filter out false/null columns and ensure non-action columns have accessorKey so they are sortable
  const safeColumns = useMemo(() => {
    return (columns || [])
      .filter(Boolean)
      .map((col) => {
        if (
          col &&
          !col.accessorKey &&
          !col.accessorFn &&
          (col.id || col.dataField)
        ) {
          const colId = col.id || col.dataField;
          const isAction =
            colId === 'actions' ||
            colId === 'action' ||
            colId === 'actionFormat' ||
            col.enableSorting === false;
          if (!isAction) {
            return {
              ...col,
              id: colId,
              accessorKey: col.dataField || col.id,
            };
          }
        }
        if (col && col.dataField && !col.accessorKey) {
          return { ...col, accessorKey: col.dataField };
        }
        return col;
      });
  }, [columns]);

  const minTableWidth = useMemo(() => {
    if (!safeColumns || safeColumns.length === 0) return undefined;
    const totalSize = safeColumns.reduce((sum, col) => sum + (col.size || 0), 0);
    return totalSize > 0 ? `${totalSize}px` : undefined;
  }, [safeColumns]);

  useEffect(() => {
    let timer;
    if (!loading && (!data || data.length === 0)) {
      timer = setTimeout(() => setShowNoData(true), 500);
    } else {
      setShowNoData(false);
    }
    return () => clearTimeout(timer);
  }, [loading, data]);

  const table = useReactTable({
    data: data || [],
    columns: safeColumns,
    defaultColumn: {
      sortingFn: smartSortingFn,
    },
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSorting,
    enableSortingRemoval: false,
  });

  const handleHeaderClick = useCallback((header, e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!header.column.getCanSort()) return;

    const targetTag = e?.target?.tagName?.toLowerCase();
    if (targetTag === 'input' || targetTag === 'select' || targetTag === 'textarea' || targetTag === 'button') {
      return;
    }

    const currentSort = header.column.getIsSorted(); // 'asc' | 'desc' | false

    if (!currentSort) {
      // First click on this column:
      // If the displayed data is already in ascending order, sort descending
      // so the user immediately sees the table sort on 1 click!
      // If not already ascending, sort ascending.
      const isAlreadyAsc = checkIfAlreadyAscending(data, header.column);
      header.column.toggleSorting(isAlreadyAsc, false);
    } else {
      // Already sorted: toggle smoothly between asc and desc on single click
      header.column.toggleSorting(currentSort === 'asc', false);
    }
  }, [data]);

  if (loading) {
    return (
      <div className="text-center p-4">
        <Loader />
      </div>
    );
  }

  const sortActiveStyle = { fontSize: '0.7rem', color: '#556ee6' };
  const sortInactiveStyle = { fontSize: '0.7rem', color: '#c3cbe4' };

  const renderHeaderContent = (header) => {
    const content = flexRender(
      header.column.columnDef.header,
      header.getContext()
    );
    const canSort = header.column.getCanSort();

    if (!canSort) {
      return <span>{content}</span>;
    }

    const sortIcon = (
      <span className="ms-1 d-inline-flex align-items-center flex-shrink-0" style={{ verticalAlign: 'middle', lineHeight: 1 }}>
        {{
          asc: <FontAwesomeIcon icon={faSortUp} style={sortActiveStyle} />,
          desc: <FontAwesomeIcon icon={faSortDown} style={sortActiveStyle} />,
        }[header.column.getIsSorted()] ?? <FontAwesomeIcon icon={faSort} style={sortInactiveStyle} />}
      </span>
    );

    if (typeof content === 'string') {
      const trimmed = content.trim();
      const lastSpaceIndex = trimmed.lastIndexOf(' ');
      if (lastSpaceIndex !== -1) {
        const firstPart = trimmed.substring(0, lastSpaceIndex + 1);
        const lastWord = trimmed.substring(lastSpaceIndex + 1);
        return (
          <span style={{ display: 'inline' }}>
            {firstPart}
            <span style={{ whiteSpace: 'nowrap' }}>
              {lastWord}
              {sortIcon}
            </span>
          </span>
        );
      }
      return (
        <span style={{ display: 'inline', whiteSpace: 'nowrap' }}>
          {trimmed}
          {sortIcon}
        </span>
      );
    }

    return (
      <span className="d-inline-flex align-items-center" style={{ maxWidth: '100%' }}>
        <span>{content}</span>
        {sortIcon}
      </span>
    );
  };

  return (
    <div className="table-responsive">
      <table className={tableClassName} style={{ tableLayout: 'fixed', width: '100%', ...(minTableWidth ? { minWidth: minTableWidth } : {}) }}>
        <thead className="table-light">
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th
                  key={header.id}
                  style={{
                    width: header.column.columnDef.size ? `${header.column.columnDef.size}px` : 'auto',
                    verticalAlign: 'top',
                    cursor: header.column.getCanSort() ? 'pointer' : 'default',
                    userSelect: 'none'
                  }}
                  onClick={(e) => handleHeaderClick(header, e)}
                >
                  <div className="d-inline-block text-start" style={{ maxWidth: '100%', cursor: header.column.getCanSort() ? 'pointer' : 'default' }}>
                    {renderHeaderContent(header)}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length > 0 ? (
            table.getRowModel().rows.map((row, rowIndex) => (
              <tr 
              key={row.id}
              style={rowStyle ? rowStyle(row.original, rowIndex) : {}}
              >
                {row.getVisibleCells().map(cell => (
                  <td
                    key={cell.id}
                    style={{
                      width: cell.column.columnDef.size ? `${cell.column.columnDef.size}px` : 'auto',
                      wordBreak: 'break-word',
                      verticalAlign: 'middle'
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={columns.length} className="text-center p-4 text-danger font-weight-bold">
                {showNoData ? noDataMessage : null}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

};

export default TanStackTable;
